/*
 * Draft 7 in the click-through preview: Loose ends, the hiring board, routes, capacity, time off, Best order and day
 * texts, with made-up data kept in memory.
 */
import { addDays, type ApplicantRow, type TimeOffRow } from "@steward/shared";
import { demoRoutes, routeRows, setRouteJobs } from "./work";

type Answer = Promise<{ status: number; body: unknown }>;
const ok = (body: unknown): Answer => Promise.resolve({ status: 200, body });
const NO = (): Answer =>
  Promise.resolve({
    status: 403,
    body: { code: "not_allowed", message: "Your role can't do that. Ask the owner if you need it." },
  });

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
let n = 0;
const id = () => `00000000-0000-4000-8000-${String(700000000000 + (n += 1))}`;

/** The Hiring v2 answers, blank unless given. */
function fresh(o: Partial<ApplicantRow>) {
  return {
    language: "en" as const,
    role: "cleaner",
    days: null,
    ride: null,
    workOk: null,
    start: null,
    textOk: false,
    src: null,
    referredBy: null,
    reason: null,
    scores: {},
    screenAt: null,
    screenConfirmed: false,
    firstTextAt: null,
    bookingLink: "https://example.com/a/demo",
    stageChangedAt: new Date().toISOString(),
    ...o,
  };
}

const applicants: ApplicantRow[] = [
  {
    id: id(),
    name: "Rosa Diaz",
    phone: "+15045550120",
    email: null,
    zip: "70115",
    position: "Cleaner",
    about: "Two years cleaning homes. Has a car.",
    stage: "new",
    source: "website",
    channel: "google",
    personId: null,
    createdAt: new Date(Date.now() - 2 * 3_600_000).toISOString(),
    ...fresh({ language: "es", days: [1, 2, 3, 4, 5], ride: true, workOk: true, textOk: true, src: "indeed" }),
  },
  {
    id: id(),
    name: "Mia Chen",
    phone: "+15045550121",
    email: null,
    zip: "70118",
    position: "Cleaner",
    about: null,
    stage: "screen",
    source: "website",
    channel: "facebook",
    personId: null,
    createdAt: new Date().toISOString(),
    ...fresh({
      ride: false,
      textOk: true,
      screenAt: new Date(Date.now() + 26 * 3_600_000).toISOString(),
      firstTextAt: new Date().toISOString(),
      src: "flyer",
    }),
  },
];
const timeOff: TimeOffRow[] = [];

/** Walkthroughs and commercial bids in the preview. */
const walks: Record<string, unknown>[] = [];
function quoteExtrasRoute(method: string, p: string, query: URLSearchParams, body: Record<string, unknown>): Answer | null {
  if (p === "walkthroughs" && method === "GET")
    return ok({
      walkthroughs: walks.filter((w) =>
        query.get("leadId")
          ? w.leadId === query.get("leadId")
          : query.get("from")
            ? Boolean(w.time) && String(w.day) >= (query.get("from") ?? "") && String(w.day) <= (query.get("to") ?? "")
            : w.quoteId === query.get("quoteId"),
      ),
    });
  if (p === "walkthroughs/team" && method === "GET") return ok([]);
  if (p === "walkthroughs" && method === "POST") {
    const w = {
      id: id(),
      clientId: null,
      leadId: body.leadId ?? null,
      quoteId: body.quoteId ?? null,
      day: body.day,
      time: body.time ?? null,
      assignedTo: body.assignedTo ?? null,
      who: null,
      forName: null,
      address: body.address ?? null,
      notes: body.notes ?? "",
      photos: [],
      createdAt: new Date().toISOString(),
    };
    walks.push(w);
    return ok(w);
  }
  if (p === "quotes/commercial")
    return Promise.resolve({
      status: 501,
      body: { code: "not_built", message: "The preview makes commercial bids only on the real server." },
    });
  return null;
}

export function fieldRoute(
  gate: (a: "schedule.update" | "people.update" | "people.invite") => boolean,
  companyId: string,
  method: string,
  p: string,
  query: URLSearchParams,
  b: unknown,
): Answer | null {
  const body = (b ?? {}) as Record<string, unknown>;
  const extra = quoteExtrasRoute(method, p, query, body);
  if (extra) return extra;
  if (p === "loose-ends" && method === "GET")
    return ok({
      looseEnds: [
        {
          key: "to_check",
          kind: "to_check",
          title: "Invoices to check",
          detail: "Look them over and send them.",
          count: 3,
          to: "/invoices",
        },
        {
          key: "new_applicants",
          kind: "new_applicants",
          title: "New job applicants",
          detail: "Waiting for a first look.",
          count: applicants.filter((a) => a.stage === "new").length,
          to: "/hr/hiring",
        },
      ].filter((e) => e.count > 0),
    });
  if (p === "loose-ends/snooze") return ok({ ok: true });
  if (p === "applicants" && method === "GET") return ok({ applicants });
  if (p === "applicants/pool-text") return ok({ sent: applicants.filter((a) => a.stage === "not_now" && a.textOk).length });
  if (p.startsWith("applicants/")) {
    const a = applicants.find((x) => x.id === p.split("/")[1]);
    if (!a) return null;
    if (p.endsWith("/hire")) {
      if (!gate("people.invite")) return NO();
      Object.assign(a, { stage: "hired", personId: id() });
      return ok(a);
    }
    if (!gate("people.update")) return NO();
    if (p.endsWith("/text-link")) {
      Object.assign(a, { firstTextAt: new Date().toISOString(), stage: a.stage === "new" ? "talking" : a.stage });
      return ok(a);
    }
    const { tell: _tell, scores, ...rest } = body as Partial<ApplicantRow> & { tell?: boolean };
    Object.assign(a, rest, scores ? { scores: { ...a.scores, ...scores } } : {});
    return ok(a);
  }
  if (p === "routes" && method === "GET") return ok({ routes: routeRows(companyId) });
  if (p === "routes" && method === "POST") {
    if (!gate("schedule.update")) return NO();
    const r = {
      companyId,
      id: id(),
      name: typeof body.name === "string" ? body.name : "Route",
      weekday: Number(body.weekday) || 1,
      crew: Array.isArray(body.crew) ? (body.crew as string[]) : [],
      color: typeof body.color === "string" ? body.color : null,
      active: true,
    };
    demoRoutes.push(r);
    return ok(routeRows(companyId).find((x) => x.id === r.id));
  }
  if (p.startsWith("routes/")) {
    const rid = p.split("/")[1];
    const r = demoRoutes.find((x) => x.id === rid && x.companyId === companyId);
    if (!r) return null;
    const row = () => ok(routeRows(companyId).find((x) => x.id === r.id));
    if (p.endsWith("/move")) {
      const count = routeRows(companyId).find((x) => x.id === r.id)?.jobs.length ?? 0;
      return ok({ moved: count, texted: body.message ? count : 0 });
    }
    if (!gate("schedule.update")) return NO();
    if (p.endsWith("/order")) {
      setRouteJobs(r.id, (body.jobIds as string[] | undefined) ?? []);
      return row();
    }
    if (method === "DELETE") {
      const count = routeRows(companyId).find((x) => x.id === r.id)?.jobs.length ?? 0;
      setRouteJobs(r.id, []);
      demoRoutes.splice(demoRoutes.indexOf(r), 1);
      return ok({ ok: true, jobs: count });
    }
    for (const k of ["name", "weekday", "crew", "color", "active"] as const) if (k in body) Object.assign(r, { [k]: body[k] });
    return row();
  }
  if (p === "capacity") {
    const from = query.get("from") ?? today();
    return ok({
      on: true,
      days: Array.from({ length: 14 }, (_, i) => {
        const d = addDays(from, i);
        const wd = new Date(`${d}T12:00:00Z`).getUTCDay();
        const teams = wd === 0 || wd === 6 ? 0 : 2;
        const booked = teams ? 6 + ((i * 5) % 28) : 0;
        return {
          date: d,
          teams,
          people: teams * 2,
          hoursHeld: teams * 16,
          hoursBooked: booked,
          visits: Math.round(booked / 3),
          open: teams > 0 && booked + 3 <= teams * 16,
          morning: teams > 0 && booked < teams * 8,
          afternoon: teams > 0 && booked + 3 <= teams * 16,
        };
      }),
    });
  }
  // A crew member's own time off (the preview has one made-up crew login).
  if (p === "my-time-off" && method === "GET") return ok({ timeOff: timeOff.filter((t) => t.personName === "You") });
  if (p === "my-time-off" && method === "POST") {
    const t: TimeOffRow = {
      id: id(),
      personId: id(),
      personName: "You",
      fromDay: String(body.fromDay),
      toDay: String(body.toDay),
      fromTime: (body.fromTime as string | undefined) ?? null,
      toTime: (body.toTime as string | undefined) ?? null,
      reason: (body.reason as string | undefined) ?? null,
    };
    timeOff.push(t);
    return ok(t);
  }
  if (p.startsWith("my-time-off/")) {
    const i = timeOff.findIndex((x) => x.id === p.split("/")[1] && x.personName === "You");
    if (i >= 0) timeOff.splice(i, 1);
    return ok({ ok: true });
  }
  if (p === "time-off" && method === "GET") return ok({ timeOff });
  if (p === "time-off" && method === "POST") {
    const t: TimeOffRow = {
      id: id(),
      personId: String(body.personId),
      personName: "Team member",
      fromDay: String(body.fromDay),
      toDay: String(body.toDay),
      fromTime: (body.fromTime as string | undefined) ?? null,
      toTime: (body.toTime as string | undefined) ?? null,
      reason: null,
    };
    timeOff.push(t);
    return ok(t);
  }
  if (p.startsWith("time-off/")) {
    const i = timeOff.findIndex((x) => x.id === p.split("/")[1]);
    if (i >= 0) timeOff.splice(i, 1);
    return ok({ ok: true });
  }
  if (p === "day-order" && method === "GET") return ok({ refs: [] });
  if (p === "day-order") return ok({ refs: body.refs });
  // No roads in the demo: the map keeps its straight dashes.
  if (p === "route-path") return ok({ points: [], minutes: 0, miles: 0, roads: false, home: null });
  if (p === "best-order")
    return ok({
      // Made up: the last stop moves to the front.
      order: (() => {
        const refs = [...((body.refs as string[] | undefined) ?? [])];
        return refs.length > 1 ? [refs.pop()!, ...refs] : refs;
      })(),
      now: { minutes: 74, miles: 31 },
      best: { minutes: 52, miles: 22 },
      savedMinutes: 22,
      estimate: false,
    });
  if (p === "day-text")
    return ok({
      sent: body.dry ? 0 : 3,
      skipped: [{ clientId: id(), name: "Dan West", why: "Texted STOP" }],
      samples: [
        { name: "Ann Lee", to: "+15045550101", text: "Hi Ann, a note about your lawn care on Tuesday, Nov 3." },
        { name: "Ben Fox", to: "+15045550102", text: "Hi Ben, a note about your lawn care on Tuesday, Nov 3." },
      ],
    });
  return null;
}

const ago = (min: number) => new Date(Date.now() - min * 60_000).toISOString();
const threads = [
  {
    id: id(),
    channel: "sms" as const,
    name: "Ann Lee",
    contact: "+15045550101",
    clientId: null,
    status: "open" as const,
    assignedTo: null,
    unread: 1,
    lastAt: ago(4),
    lastText: "Can you come Friday instead?",
    stopped: false,
    messages: [
      {
        id: id(),
        direction: "out",
        author: "auto",
        from: "Automatic",
        body: "Hi Ann, just a friendly reminder that your deep clean is scheduled tomorrow.",
        at: ago(1440),
        status: "dry_run",
      },
      {
        id: id(),
        direction: "in",
        author: "client",
        from: "Ann Lee",
        body: "Can you come Friday instead?",
        at: ago(4),
        status: null,
      },
      {
        id: id(),
        direction: "note",
        author: "eunice",
        from: "Eunice",
        body: "Practice. Eunice would say: Let me check Friday and get back to you.",
        at: ago(4),
        status: null,
      },
    ],
  },
  {
    id: id(),
    channel: "sms" as const,
    name: "+15045550188",
    contact: "+15045550188",
    clientId: null,
    status: "open" as const,
    assignedTo: null,
    unread: 0,
    lastAt: ago(90),
    lastText: "Hi, this is The Cleaners. Sorry we missed your call!",
    stopped: false,
    messages: [
      {
        id: id(),
        direction: "out",
        author: "eunice",
        from: "Eunice",
        body: "Hi, this is The Cleaners. Sorry we missed your call! What can we help you with?",
        at: ago(90),
        status: "dry_run",
      },
    ],
  },
];

/** The Inbox and Eunice in the preview. */
export function inboxRoute(method: string, p: string, b: unknown, query?: URLSearchParams): Answer | null {
  const body = (b ?? {}) as Record<string, unknown>;
  const row = (t: (typeof threads)[number]) => ({ ...t, messages: undefined });
  if (p === "inbox/unread") return ok({ count: threads.filter((t) => t.unread > 0).length });
  if (p === "alerts" || p === "alerts/read") return ok({ alerts: [], unread: 0 });
  if (p === "inbox/threads") {
    const clientId = query?.get("clientId");
    return ok({ threads: threads.filter((t) => !clientId || (t.clientId as string | null) === clientId).map(row) });
  }
  if (p.startsWith("inbox/messages/") && p.endsWith("/undo")) {
    const mid = p.split("/")[2];
    for (const t of threads) {
      const i = t.messages.findIndex((m) => m.id === mid);
      if (i >= 0) return ok({ body: t.messages.splice(i, 1)[0]?.body ?? "" });
    }
    return null;
  }
  if (p.startsWith("inbox/threads/")) {
    const [, , tid, what] = p.split("/");
    const t = threads.find((x) => x.id === tid);
    if (!t) return null;
    if (what === "reply" || what === "note") {
      t.messages.push({
        id: id(),
        direction: what === "note" ? "note" : "out",
        author: "team",
        from: "Team",
        body: typeof body.body === "string" ? body.body : "",
        at: new Date().toISOString(),
        status: what === "note" ? null : "dry_run",
      });
      t.lastText = typeof body.body === "string" ? body.body : "";
    }
    if (method === "PATCH") Object.assign(t, { status: body.status ?? t.status });
    t.unread = 0;
    return ok(method === "PATCH" ? row(t) : t);
  }
  if (p === "eunice")
    return ok({
      runs: [
        {
          id: id(),
          threadId: threads[0]!.id,
          contact: "+15045550101",
          mode: "practice",
          category: "existing_customer",
          reply: "Let me check Friday and get back to you.",
          sent: false,
          alert: true,
          alertSummary: "Ann wants to move to Friday",
          skipped: null,
          at: ago(4),
        },
      ],
      lessons: [{ id: id(), text: "Never offer Sunday visits.", status: "approved", createdAt: ago(3000) }],
      usedThisMonth: 37,
      allowance: 500,
      webhookUrl: null,
    });
  if (p.startsWith("eunice/lessons"))
    return ok({
      id: id(),
      text: typeof body.text === "string" ? body.text : "Lesson",
      status: body.status ?? "proposed",
      createdAt: new Date().toISOString(),
    });
  return null;
}

/** Opened from a pay or quote link in the preview: look only until any 6 digit code. */
let myByLink = false;

/** The Cleaners requests in the preview hub. Get a quote adds to it, so a sent request shows up in the list. */
interface DemoRequest {
  id: string;
  title: string;
  askedOn: string;
  state: "new" | "sent" | "converted" | "lost";
  what: string | null;
  wantsOn: string | null;
  address: string | null;
  photos: number;
  quote: { number: number; link: string } | null;
}
let myRequests: DemoRequest[] | null = null;
const demoRequests = (t: string): DemoRequest[] =>
  (myRequests ??= [
    {
      id: "00000000-0000-4000-8000-0000000000a1",
      title: "Deep clean before the holidays",
      askedOn: addDays(t, -3),
      state: "sent",
      what: "Deep clean before the holidays. Family comes in two weeks. Kitchen and both baths need the most work.",
      wantsOn: addDays(t, 12),
      address: "8616 Oak St, Apt 401, New Orleans, LA 70118",
      photos: 2,
      quote: { number: 2004, link: "#quote" },
    },
    {
      id: "00000000-0000-4000-8000-0000000000a2",
      title: "Move out clean, 2 bedroom",
      askedOn: addDays(t, -40),
      state: "converted",
      what: "Move out clean, 2 bedroom. Need it done before the walk through.",
      wantsOn: addDays(t, -35),
      address: "4420 Magazine St, New Orleans, LA 70115",
      photos: 0,
      quote: { number: 1988, link: "#quote" },
    },
    {
      id: "00000000-0000-4000-8000-0000000000a3",
      title: "Windows inside and out",
      askedOn: addDays(t, -70),
      state: "lost",
      what: null,
      wantsOn: null,
      address: null,
      photos: 0,
      quote: null,
    },
  ]);

/** My Trades in the preview: any phone and any 6 digit code sign in to a made-up client of The Cleaners and The Marketers. */
export function myRoute(method: string, p: string, body?: unknown): Answer | null {
  // View as client: the office sees the same made-up client, for its own company only.
  if (/^clients\/[^/]+\/hub$/.test(p))
    return (myRoute("GET", "public/my") ?? ok({})).then((r) => {
      const b = r.body as { brands: unknown[]; others: unknown[] };
      return { ...r, body: { ...b, brands: b.brands.slice(0, 1) } };
    });
  if (!p.startsWith("public/my")) return null;
  if (p === "public/my/code" || p === "public/my/sign-out") return ok({ ok: true });
  if (p === "public/my/link") {
    myByLink = true;
    return ok({ token: "e".repeat(64) });
  }
  if (p === "public/my/verify") {
    myByLink = false;
    return ok({ token: "f".repeat(64) });
  }
  if (p === "public/my/request") {
    const b = (body ?? {}) as { companyId?: string; what?: string; day?: string; place?: Record<string, string> };
    const what = (b.what ?? "").replace(/s+/g, " ").trim();
    const reqId = id();
    // Only The Cleaners keeps a list in the preview; a sister brand just says thanks.
    if (b.companyId === "00000000-0000-4000-8000-0000000000f1") {
      const pl = b.place;
      demoRequests(today()).unshift({
        id: reqId,
        title: what.length > 80 ? `${what.slice(0, 77)}...` : what,
        askedOn: today(),
        state: "new",
        what,
        wantsOn: b.day ?? null,
        address: pl
          ? [pl.street1, pl.street2, pl.city, [pl.state, pl.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")
          : "8616 Oak St #401 70118",
        photos: 0,
        quote: null,
      });
    }
    return ok({
      ok: true,
      business: b.companyId ? "The Cleaners" : "The GroundsMen",
      id: reqId,
      companyId: b.companyId ?? "00000000-0000-4000-8000-0000000000f3",
    });
  }
  if (p === "public/my/request-photo") {
    const b = (body ?? {}) as { requestId?: string };
    const r = demoRequests(today()).find((x) => x.id === b.requestId);
    if (r) r.photos += 1;
    return ok({ ok: true, photos: r?.photos ?? 1 });
  }
  if (p === "public/my/card-start") return ok({ intentId: "seti_demo", clientSecret: "demo", publishableKey: null, fake: true });
  if (p === "public/my/card-done") return ok({ ok: true, saved: true });
  if (p.startsWith("public/my/open-days")) {
    const t = today();
    return ok({ days: [2, 3, 5, 6, 9, 10, 12, 13].map((n) => addDays(t, n)) });
  }
  if (p === "public/my/visits" && method === "POST") return ok({ ok: true, jobId: id(), waiting: false });
  if (p === "public/my/visit-addons" && method === "PUT") return ok({ ok: true, totalCents: 15500 });
  const t = today();
  const visit = (n: number, job = "Weekly House Cleaning") => ({
    ref: `demo.${String(n)}`,
    date: addDays(t, n),
    startTime: "13:00:00",
    endTime: "15:00:00",
    arriveFrom: "13:00:00",
    arriveTo: "14:00:00",
    job,
    crew: ["Kenia", "Rosa"],
    address: "8616 Oak St #401, New Orleans, LA 70118",
    serviceId: "std",
    addonsOpen: true,
    addons: [] as { id: string; count: number; cents: number }[],
  });
  const inv = (n: number, ago: number, state: "open" | "past_due" | "paid") => ({
    number: n,
    title: "Weekly House Cleaning",
    issuedOn: addDays(t, -ago),
    dueOn: addDays(t, -ago),
    paidOn: state === "paid" ? addDays(t, -ago) : null,
    totalCents: 12000,
    balanceCents: state === "paid" ? 0 : 12000,
    state,
    payLink: "#pay",
  });
  const brand = {
    quoter: false,
    planOffer: null as unknown,
    plans: [] as unknown[],
    requests: [] as unknown[],
    quotes: [] as unknown[],
    refer: null,
    results: null,
    cardsOn: true,
    url: window.location.origin,
    firstName: "Ann",
    address: "8616 Oak St #401 70118",
    logoUrl: null,
  };
  return ok({
    brands: [
      {
        ...brand,
        companyId: "00000000-0000-4000-8000-0000000000f1",
        name: "The Cleaners",
        trade: "cleaning",
        color: "#00929F",
        deep: "#00737D",
        foot: "#0B4F55",
        logoUrl: "https://thecleanersusa.com/wp-content/uploads/2026/09/The-Cleaners-Logo-White-1024x349.png",
        phone: "+15045550100",
        email: "hello@thecleanersusa.com",
        card: { brand: "Visa", last4: "4242", auto: true },
        next: [6, 13, 20, 27, 34].map((n, i) =>
          i === 1 ? { ...visit(n), addons: [{ id: "fridge", count: 2, cents: 3000 }] } : visit(n),
        ),
        past: [-1, -8, -15].map((n, i) => {
          const { serviceId: _s, addonsOpen: _o, addons: _a, ...v } = visit(n);
          return { ...v, report: i === 0 ? "#report" : null };
        }),
        addons: [
          { id: "oven", name: "Inside the oven", cents: 3500, planCents: 2500, each: false, only: null, about: null },
          {
            id: "fridge",
            name: "Inside the fridge",
            cents: 1500,
            planCents: 1000,
            each: true,
            only: null,
            about: "Shelves, drawers and door",
          },
          { id: "cabinets", name: "Inside the cabinets", cents: 4000, planCents: 2500, each: false, only: null, about: null },
          { id: "walls", name: "Wash walls", cents: 5000, planCents: null, each: false, only: "deep", about: null },
        ],
        invoices: [inv(1121, 21, "past_due"), inv(1126, 19, "paid"), inv(1117, 26, "paid"), inv(1101, 41, "paid")],
        quotes: [
          {
            number: 2004,
            title: "Deep clean before the holidays",
            status: "sent",
            sentOn: addDays(t, -2),
            totalCents: 32000,
            link: "#quote",
          },
        ],
        requests: demoRequests(t),
        // The made-up client had a deep clean and has no schedule yet: the preview shows the quoter and Keep it this clean.
        quoter: true,
        planOffer: {
          prices: [
            { id: "biweekly", label: "Every two weeks", perVisitCents: 11900 },
            { id: "weekly", label: "Every week", perVisitCents: 10500 },
            { id: "monthly", label: "Once a month", perVisitCents: 14000 },
          ],
        },
        plans: [],
        refer: { link: "https://thecleanersusa.com/?ref=A1B2C3D4E5", amountCents: 2500 },
      },
      {
        ...brand,
        companyId: "00000000-0000-4000-8000-0000000000f4",
        name: "The Marketers",
        trade: "agency",
        color: "#0A0A0A",
        deep: "#111111",
        foot: "#0A0A0A",
        phone: "+15045550300",
        email: null,
        card: null,
        next: [],
        past: [],
        addons: [],
        invoices: [{ ...inv(3012, 6, "paid"), title: "Facebook ads", totalCents: 200000 }],
        // An agency client sees their own results (owner answer 119).
        results: {
          health: "good",
          leadsNow: 23,
          leadsBefore: 19,
          costPerLeadCents: 4100,
          reviews: 88,
          rating: 4.8,
          rankNow: 3.2,
          rankBefore: 4.1,
          planCents: 150000,
        },
      },
    ],
    others: [
      {
        brandId: "00000000-0000-4000-8000-0000000000f5",
        name: "The GroundsMen",
        trade: "lawn",
        color: "#367C2B",
        logoUrl: "https://thegroundsmen.com/wp-content/uploads/2026/09/The-GroundsMen-Logo-04-1024x297.png",
        website: "https://thegroundsmen.com",
        needsZip: false,
      },
      {
        brandId: "00000000-0000-4000-8000-0000000000f6",
        name: "The Washers",
        trade: "washers",
        color: "#005EB8",
        logoUrl: "https://exteriorwashers.com/wp-content/uploads/2026/09/The-Washers-04-1-1024x349.png",
        website: "https://exteriorwashers.com",
        needsZip: true,
      },
    ],
    ...(myByLink ? { byLink: true, phoneEnd: "4550" } : {}),
  });
}

/** The client page's Email and Text buttons in the preview: that client's conversation, made the first time. */
export function demoThreadFor(clientId: string, name: string, channel: "sms" | "email", contact: string): string {
  const have = threads.find((t) => (t.clientId as string | null) === clientId && (t.channel as string) === channel);
  if (have) return have.id;
  const made = { ...threads[0]!, id: id(), channel, name, contact, clientId, unread: 0, lastAt: new Date().toISOString() };
  threads.unshift({ ...made, lastText: "", messages: [] } as unknown as (typeof threads)[number]);
  return made.id;
}
